/**
 * Impersonation-Hook ("Act-as", Slice 3 UI).
 *
 * Liest den reichen Auth-Status (`auth.whoami`) und kapselt Start/Stop. Nach
 * einem Identitätswechsel wird die passende Rollen-Startseite hart geladen —
 * so re-evaluieren Router-Guards (requireRole) UND alle React-Query-Caches unter
 * der neuen effektiven Identität, statt fragil einzeln invalidiert zu werden.
 */
import { toast } from 'sonner';
import { trpc } from '../lib/trpc.js';

export function useImpersonation() {
  const whoami = trpc.auth.whoami.useQuery(undefined, {
    retry: false,
    refetchOnWindowFocus: false,
  });

  const start = trpc.auth.startImpersonation.useMutation({
    onError: (e) => toast.error(`Impersonation fehlgeschlagen: ${e.message}`),
  });

  const startAs = (targetUserId: string, destination: '/worker' | '/client' | '/') =>
    start.mutate({ targetUserId }, { onSuccess: () => window.location.assign(destination) });

  const ensureClientTarget = trpc.clients.ensureImpersonationTarget.useMutation({
    onError: (e) => toast.error(`Kundensicht konnte nicht vorbereitet werden: ${e.message}`),
    onSuccess: ({ targetUserId }) => startAs(targetUserId, '/client'),
  });

  const stop = trpc.auth.stopImpersonation.useMutation({
    onError: (e) => toast.error(`Beenden fehlgeschlagen: ${e.message}`),
    onSuccess: () => window.location.assign('/'),
  });

  const data = whoami.data;
  return {
    impersonating: data?.impersonating ?? false,
    realUser: data?.realUser ?? null,
    /** Effektive Identität — bei Impersonation der Ziel-User. */
    actingAs: data?.user ?? null,
    loading: whoami.isLoading,
    starting: start.isPending || ensureClientTarget.isPending,
    stopping: stop.isPending,
    start: (targetUserId: string) => startAs(targetUserId, '/'),
    startWorker: (targetUserId: string) => startAs(targetUserId, '/worker'),
    startClient: (clientId: string) => ensureClientTarget.mutate({ clientId }),
    stop: () => stop.mutate(),
  };
}
